import { environment } from '@environments/environment';
import { HttpParams, HttpClient } from '@angular/common/http';
import { of, forkJoin, BehaviorSubject, Observable, interval, Subscription, timer } from 'rxjs';
import { mergeMap, tap, filter, map, distinctUntilChanged, switchMap } from 'rxjs/operators';
import * as hash from 'object-hash';


/**
 * Abstract class for the entities
 * that must be used into the views of wa_app
 *
 * @export
 * @class EntitiesStore
 * @template T
 * @template ParamsType
 */
export class EntitiesStore<T, ParamsType = any> {

  /**
   * The pagination data that is retuned from the api
   *
   * @type {{
   *     totalResults: number,
   *     resultsPerPage: number,
   *     page: number
   *   }}
   * @memberof EntitiesStore
   */
  pagination: {
    totalResults: number,
    resultsPerPage: number,
    page: number
  } = {} as any

  /**
   * Data stream of received data
   *
   * @memberof EntitiesStore
   */
  dataStream = new BehaviorSubject(undefined);
  /**
   * The data stored into the array
   *
   * @type {Array<T>}
   * @memberof EntitiesStore
   */
  data: Array<T> = [];

  /**
   * The reference to know if the data is loading
   *
   * @memberof EntitiesStore
   */
  loadingRef = true;

  /**
   * The value to check if the data must reset the pagination or not
   *
   * @memberof EntitiesStore
   */
  requestMore = false;

  liveSubject = new BehaviorSubject(this.data);

  liveSubscription: Subscription = undefined;

  constructor(protected endpoint_name: string, protected http: HttpClient,
    public dependencies?: Array<{ entity: EntitiesStore<any>, params?: any, isLive?: boolean, time?: number }>) {
  }

  /**
   * A wrapper for perform live petitions with a window time.
   *
   * @protected
   * @param {boolean} isLive
   * @memberof EntitiesStore
   */
  protected liveWrapper(params: any, request_more: boolean, isLive: boolean, time: number) {
    if (!isLive) {
      return this.getRequest(params, request_more);
    } else {
      if (params) {
        // Asign the live flag for interceptor.
        Object.assign(params, { 'live_flag': true });
      }
      if (this.liveSubscription) {
        //this.loadingRef = true;
        this.liveSubscription.unsubscribe();
      }
      let hashOldData = hash(this.data);
      this.liveSubscription = timer(0, time).pipe(
        tap(a => {
          hashOldData = hash(this.data);
        }),
        // Perform request.
        switchMap(a => this.getRequest(params, request_more).pipe(
          tap(a => {
            this.liveSubject.next(a.items);
          })
        )),
        // Add internal cache for check that data hasn't changed and avoid flickering on screen.

      ).subscribe();
      return this.dataStream.asObservable().pipe(
        filter(value => value != undefined),
        distinctUntilChanged((f, s) => {
          return false;
        }));

    }
  }


  /**
   * The method for request data
   *
   * @param {ParamsType} params
   * @param {boolean} request_more
   * @memberof EntitiesStore
   */
  requestData(params: ParamsType, request_more: boolean, isLive = false, time = 0): Observable<any> {
    this.loadingRef = true;
    this.requestMore = request_more;
    if (this.dependencies && this.dependencies.length > 0) {
      return of(this.dependencies)
        .pipe(
          mergeMap(
            a =>
              forkJoin(...a.map(dependency => dependency.entity.requestData(dependency.params, false, dependency.isLive, dependency.time)))
          ),
          mergeMap(a => this.liveWrapper(params, request_more, isLive, time))
        )
    } else {
      return this.liveWrapper(params, request_more, isLive, time);
    }
  }
  protected getRequest(params: ParamsType, request_more: boolean) {
    let page = (((this.data.length === this.pagination.totalResults) && this.data.length !== 0) ? this.pagination.totalResults :
      request_more === true ? this.pagination.resultsPerPage + 10 : 10);
    page = page < 10 ? 10 : page;
    return this.http.get<{ pageInfo: any, items: Array<T> }>(environment.api_url,
      {
        params: new HttpParams({ fromObject: params as any })
          .append('content', this.endpoint_name)
          .append('v', '3')
          .append('RBP', page.toString())
      }
    ).pipe(
      tap(next => {
      this.dataStream.next(next.items);
      this.data = next.items;
      this.pagination = next.pageInfo;
      this.loadingRef = false;
      this.requestMore = false;
    }));
  }

  /**
   * Get data as stream for use with RxJs
   *
   * @readonly
   * @type {Observable<Array<T>>}
   * @memberof EntitiesStore
   */
  get observableData(): Observable<Array<T>> {
    return this.dataStream.asObservable().pipe(filter(a => a != undefined));
  }
}
